import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { CheckCircle, XCircle, AlertTriangle, Paperclip, RefreshCw, Trash2 } from 'lucide-react'
import { Button, Chip, Drawer, Textarea } from '@/components/ui'
import {
  useApproveSubmission,
  useCloseAsDuplicate,
  useDeleteLineCoaDocument,
  useDeleteSubmissionDocument,
  useRejectSubmission,
  useRetrySubmission,
  useSetRoutingMode,
  useSubmission,
  useUpdateSubmissionNote,
  useUploadLineCoaDocument,
  useUploadSubmissionDocument,
} from '../api/queries'
import { ORDER_TYPE_LABELS, type DocType, type RoutingMode, type SubmissionDocument, type SubmissionSummary, type ValidationResult } from '../api/types'
import { directDeliveryStatus } from '../utils/delivery-status'
import { shouldShowLastError } from '../utils/should-show-last-error'
import { displayStatus } from '../utils/status-meta'
import { missingCofaLots } from '../utils/missing-cofa'

const ODOO_BASE = 'https://www.siliconesplus.com/web'

function odooPickingUrl(id: number) {
  return `${ODOO_BASE}#model=stock.picking&id=${id}&view_type=form`
}

function odooLotUrl(id: number) {
  return `${ODOO_BASE}#model=stock.lot&id=${id}&view_type=form`
}

// Mirrors middleware/app/services/uviva_inbound.py's NO_PROCESSABLE_RECEIPT_PREFIX
// — a posting failure with this shape is guaranteed to fail identically on
// retry (the PO line has nothing left to receive against), so the UI
// de-emphasizes Retry and points at Reject instead of implying "try again"
// might work.
const NO_PROCESSABLE_RECEIPT_PREFIX = 'NO_PROCESSABLE_RECEIPT: '

function displayedLastError(lastError: string | null | undefined): string | null | undefined {
  if (lastError?.startsWith(NO_PROCESSABLE_RECEIPT_PREFIX)) {
    return lastError.slice(NO_PROCESSABLE_RECEIPT_PREFIX.length)
  }
  return lastError
}

function DocumentRow({ doc, onRemove }: { doc: SubmissionDocument; onRemove?: () => void }) {
  return (
    <li className="flex items-center gap-2 rounded border border-border-subtle bg-surface px-3 py-1.5 text-sm">
      <Paperclip size={14} className="text-secondary" />
      <a
        href={`/api/documents/${doc.document_id}/${encodeURIComponent(doc.filename)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 truncate text-link hover:underline"
      >
        {doc.filename}
      </a>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${doc.filename}`}
          className="text-tertiary hover:text-danger"
        >
          <Trash2 size={14} />
        </button>
      ) : null}
    </li>
  )
}

function AttachLink({ label, onFile }: { label: string; onFile: (file: File) => void }) {
  return (
    <label className="cursor-pointer text-xs text-link hover:underline">
      {label}
      <input
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
        }}
      />
    </label>
  )
}

export interface SubmissionDetailDrawerProps {
  submission: SubmissionSummary | null
  onClose: () => void
  canApprove?: boolean
  /** admin/operations/uviva (own submission) — can attach/replace/remove
   *  documents. regulatory/finance get read-only visibility. */
  canManageDocuments?: boolean
  /** The vendor viewing their own submission — hides internal exception
   *  text, PO-matching rule codes, and ops' internal note; shows the
   *  simplified 2-bucket vendor_status instead of the internal enum. */
  isVendorView?: boolean
}

export function SubmissionDetailDrawer({
  submission,
  onClose,
  canApprove = false,
  canManageDocuments = false,
  isVendorView = false,
}: SubmissionDetailDrawerProps) {
  const [approveNote, setApproveNote] = useState('')
  const [rejectNote, setRejectNote] = useState('')
  const [duplicateNote, setDuplicateNote] = useState('')
  const [mode, setMode] = useState<'view' | 'approving' | 'rejecting' | 'closing_duplicate'>('view')

  // Live-poll while open so an async approve/retry outcome (posted vs.
  // post_failed) shows up without the user having to manually close and
  // reopen the drawer — see useSubmission's refetchInterval. Falls back to
  // the row snapshot the parent passed in until the live fetch resolves.
  const { data: liveSubmission } = useSubmission(submission?.id ?? null)
  const current = liveSubmission ?? submission

  const approve = useApproveSubmission()
  const reject = useRejectSubmission()
  const closeAsDuplicate = useCloseAsDuplicate()
  const retry = useRetrySubmission()
  const setRoutingMode = useSetRoutingMode()
  const updateNote = useUpdateSubmissionNote()
  const uploadDoc = useUploadSubmissionDocument()
  const deleteDoc = useDeleteSubmissionDocument()
  const uploadCoa = useUploadLineCoaDocument()
  const deleteCoa = useDeleteLineCoaDocument()

  // The shared notes field is CONTROLLED and seeded from the server ONLY
  // when the open submission changes — never on a background refetch —
  // so polling (see useSubmission above) can't wipe out an in-progress
  // edit, mirroring sales-inbox/SalesIntakeWorkView.tsx's header-field
  // seeding pattern.
  const [noteText, setNoteText] = useState('')
  const seededNoteForRef = useRef<string | null>(null)
  useEffect(() => {
    if (current && seededNoteForRef.current !== current.id) {
      setNoteText(current.notes ?? '')
      seededNoteForRef.current = current.id
    }
  }, [current])

  function handleNoteBlur() {
    if (!current) return
    const v = noteText.trim()
    if (v !== (current.notes ?? '')) {
      updateNote.mutate({ id: current.id, note: v })
    }
  }

  const isPendingReview = current?.status === 'pending_review'
  const isPostFailed = current?.status === 'post_failed'
  const isValidationFailed = current?.status === 'validation_failed'
  // Failed rules across all lines, de-duplicated by rule code, with the
  // server's plain-language guidance. blockingFix = a failure that must be
  // fixed in Odoo (Approve would stage lots on the wrongly-routed receipt).
  const failedRules = (() => {
    const seen = new Map<string, ValidationResult>()
    for (const ln of current?.lines ?? []) {
      for (const r of ln.validation_results ?? []) {
        if (!r.passed && !seen.has(r.rule_code)) seen.set(r.rule_code, r)
      }
    }
    return Array.from(seen.values())
  })()
  const blockingFix = failedRules.some((r) => r.kind === 'fix_in_odoo')
  const hasDuplicateFailure = failedRules.some((r) => r.kind === 'duplicate')
  // Every lot needs its CofA: the server refuses approval outright, so the
  // Approve button is not offered at all.
  const lotsMissingCofa = missingCofaLots(current?.lines)
  const isLikelyDuplicate = Boolean(current?.last_error?.startsWith(NO_PROCESSABLE_RECEIPT_PREFIX))

  // Toast the eventual outcome of an approve/retry cycle — the mutation
  // itself only kicks off an async Celery post, so the real result only
  // shows up once polling picks up the status transition.
  const prevCycleRef = useRef<{ id: string; status: string } | null>(null)
  useEffect(() => {
    if (!current) {
      prevCycleRef.current = null
      return
    }
    const prev = prevCycleRef.current
    if (prev && prev.id === current.id && prev.status !== current.status
      && (prev.status === 'posting' || prev.status === 'validating')) {
      if (current.status === 'posted') {
        toast.success(`${current.shipment_ref} posted to Odoo`)
      } else if (current.status === 'post_failed' || current.status === 'validation_failed') {
        toast.error(`${current.shipment_ref} failed to post — see details below`, { duration: 8000 })
      }
    }
    prevCycleRef.current = { id: current.id, status: current.status }
  }, [current])

  function handleUploadDoc(docType: DocType, file: File) {
    if (!current) return
    uploadDoc.mutate({ submissionId: current.id, docType, file })
  }
  function handleRemoveDoc(documentId: number) {
    if (!current) return
    deleteDoc.mutate({ submissionId: current.id, documentId })
  }
  function handleUploadCoa(lineId: number, file: File) {
    if (!current) return
    uploadCoa.mutate({ submissionId: current.id, lineId, file })
  }
  function handleRemoveCoa(lineId: number, documentId: number) {
    if (!current) return
    deleteCoa.mutate({ submissionId: current.id, lineId, documentId })
  }

  async function handleApprove() {
    if (!current) return
    try {
      await approve.mutateAsync({ id: current.id, note: approveNote, override: blockingFix })
      toast.success('Approved — submitting to Odoo…')
      setMode('view')
      setApproveNote('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Approve failed')
    }
  }

  async function handleReject() {
    if (!current || !rejectNote.trim()) return
    try {
      await reject.mutateAsync({ id: current.id, note: rejectNote })
      toast.success('Submission rejected')
      setMode('view')
      setRejectNote('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Reject failed')
    }
  }

  async function handleCloseAsDuplicate() {
    if (!current || !duplicateNote.trim()) return
    try {
      await closeAsDuplicate.mutateAsync({ id: current.id, note: duplicateNote })
      toast.success('Closed as duplicate — marked Posted')
      setMode('view')
      setDuplicateNote('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Close as duplicate failed')
    }
  }

  async function handleRetry() {
    if (!current) return
    try {
      await retry.mutateAsync(current.id)
      toast.success('Retrying…')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed')
    }
  }

  return (
    <Drawer
      open={submission != null}
      onOpenChange={(open) => { if (!open) onClose() }}
      size="wide"
      title={current?.shipment_ref ?? ''}
      meta={current && (
        <div className="flex items-center gap-2">
          {(() => {
            const { label, variant } = displayStatus(current, isVendorView)
            return (
              <Chip variant={variant} size="xs">
                {label}
              </Chip>
            )
          })()}
          <Chip
            variant={
              current.order_type === 'uviva_direct'
                ? 'info'
                : current.order_type === 'uvwh_intake'
                ? 'warning'
                : 'neutral'
            }
            size="xs"
          >
            {ORDER_TYPE_LABELS[current.order_type]}
          </Chip>
          {current.po_ref && (
            <span className="text-role-metadata">PO: {current.po_ref}</span>
          )}
          {current.order_type !== 'uviva_direct' && current.destination_warehouse_name && (
            <span className="text-role-metadata">Destination: {current.destination_warehouse_name}</span>
          )}
          {current.order_type === 'uviva_direct' && current.ship_to_name && (
            <span className="text-role-metadata">Ship to: {current.ship_to_name}</span>
          )}
          {current.order_ref && (
            <span className="text-role-metadata">· {current.order_ref}</span>
          )}
          {current.order_type === 'spi_order' && canApprove && !isVendorView && isPendingReview && (
            <label className="flex items-center gap-1 text-role-metadata">
              Receiving as:
              <select
                aria-label="Receiving as"
                className="rounded border px-1 py-0.5"
                value={current.routing_mode ?? ''}
                disabled={setRoutingMode.isPending}
                onChange={(e) =>
                  setRoutingMode.mutate({
                    id: current.id,
                    routing_mode: (e.target.value || null) as RoutingMode | null,
                  })
                }
              >
                <option value="">Auto-detect</option>
                <option value="consignment">Consignment (UVWH)</option>
                <option value="standard">Standard buy (SPI)</option>
              </select>
            </label>
          )}
        </div>
      )}
    >
      {current && (
        <div className="flex flex-col gap-6 px-6 py-4">
          {/* Meta */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-role-metadata">
            <span>Submitted by: {current.submitted_by}</span>
            {current.submitted_at && (
              <span>
                {new Date(current.submitted_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </span>
            )}
            {current.resolved_by && (
              <span>Resolved by: {current.resolved_by}</span>
            )}
          </div>

          {/* Shared notes — genuinely visible AND editable by both SPI staff
              and Uviva, independent of any status transition. Distinct from
              resolution_note/internal_note below. */}
          <div className="flex flex-col gap-2">
            <h3 className="text-role-section-label">Notes</h3>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={handleNoteBlur}
              disabled={!canManageDocuments}
              placeholder="Add a note for the team…"
              className="min-h-16"
            />
          </div>

          {/* Odoo picking link */}
          {current.odoo_picking_id && (
            <a
              href={odooPickingUrl(current.odoo_picking_id)}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-link hover:underline"
            >
              {current.odoo_picking_name ?? `Picking #${current.odoo_picking_id}`} →
            </a>
          )}

          {/* Delivery status (Uviva Direct OUT operation) */}
          {current.order_type === 'uviva_direct' && current.odoo_picking_id && (() => {
            const ds = directDeliveryStatus(current)
            return (
              <div className="flex flex-col gap-2 rounded border border-border-subtle bg-surface-sunk p-3">
                <div className="flex items-center gap-2">
                  <span className="text-role-section-label">Delivery</span>
                  <Chip variant={ds.variant} size="xs">{ds.label}</Chip>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-role-metadata">
                  {current.carrier_name && <span>Carrier: {current.carrier_name}</span>}
                  {current.tracking_number && <span>Tracking: {current.tracking_number}</span>}
                  {current.delivered_at && (
                    <span>Delivered: {new Date(current.delivered_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}</span>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Error — internal roles only; the vendor's simplified status
              badge already reads "Received" while this is being sorted out. */}
          {!isVendorView && shouldShowLastError(current) && (
            <div className="flex flex-col gap-2 rounded border border-danger-border bg-danger-subtle p-3 text-sm text-danger">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{displayedLastError(current.last_error)}</span>
              </div>
              {isLikelyDuplicate && (
                <p className="pl-6 text-xs text-danger/80">
                  Retrying this posting will fail the same way every time — reject the submission instead.
                </p>
              )}
            </div>
          )}

          {/* Documents (shipment sheet / BoL / Invoice) */}
          <div className="flex flex-col gap-3">
            <h3 className="text-role-section-label">Documents</h3>
            {(() => {
              const docs = current.attached_documents ?? []
              const shipmentSheetDoc = docs.find((d) => d.doc_type === 'shipment_sheet')
              const bolDocs = docs.filter((d) => d.doc_type === 'bill_of_lading')
              const invoiceDocs = docs.filter((d) => d.doc_type === 'invoice')
              return (
                <div className="flex flex-col gap-3">
                  {shipmentSheetDoc ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-role-metadata">Shipment sheet</span>
                      <ul className="flex flex-col gap-1">
                        <DocumentRow doc={shipmentSheetDoc} />
                      </ul>
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-role-metadata">Bill of Lading</span>
                      {canManageDocuments ? (
                        <AttachLink label="+ Attach" onFile={(f) => handleUploadDoc('bill_of_lading', f)} />
                      ) : null}
                    </div>
                    {bolDocs.length === 0 ? (
                      <p className="text-xs text-tertiary">None attached.</p>
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {bolDocs.map((d) => (
                          <DocumentRow
                            key={d.document_id}
                            doc={d}
                            onRemove={canManageDocuments ? () => handleRemoveDoc(d.document_id) : undefined}
                          />
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-role-metadata">Invoice</span>
                      {canManageDocuments ? (
                        <AttachLink label="+ Attach" onFile={(f) => handleUploadDoc('invoice', f)} />
                      ) : null}
                    </div>
                    {invoiceDocs.length === 0 ? (
                      <p className="text-xs text-tertiary">None attached.</p>
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {invoiceDocs.map((d) => (
                          <DocumentRow
                            key={d.document_id}
                            doc={d}
                            onRemove={canManageDocuments ? () => handleRemoveDoc(d.document_id) : undefined}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>

          {/* Lines */}
          <div className="flex flex-col gap-3">
            <h3 className="text-role-section-label">Lines</h3>
            {(current.lines ?? []).map((ln) => {
              const failures = ln.validation_results?.filter((r) => !r.passed) ?? []
              const passes = ln.validation_results?.filter((r) => r.passed) ?? []
              return (
                <div
                  key={ln.id}
                  className="flex flex-col gap-2 rounded border border-border-subtle bg-surface-sunk p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-role-data-primary">
                      {ln.product_code}
                      {ln.lot_serial && (
                        <span className="ml-2 text-role-data-secondary">lot {ln.lot_serial}</span>
                      )}
                    </span>
                    <span className="text-role-metadata">{ln.qty_to_ship} {ln.uom ?? ''}</span>
                  </div>

                  {ln.sell_by && (
                    <span className="text-role-metadata">Sell by: {ln.sell_by}</span>
                  )}

                  {/* Internal PO-matching rule codes — plumbing the vendor
                      has no context for and shouldn't need to parse. */}
                  {!isVendorView && (
                    <div className="flex flex-col gap-1">
                      {passes.map((r) => (
                        <div key={r.rule_code} className="flex items-center gap-1.5 text-xs text-success">
                          <CheckCircle size={12} />
                          <span>{r.rule_code}{r.label ? ` · ${r.label}` : ''}</span>
                        </div>
                      ))}
                      {failures.map((r) => (
                        <div key={r.rule_code} className="flex items-start gap-1.5 text-xs text-danger">
                          <XCircle size={12} className="mt-0.5 shrink-0" />
                          <span>
                            [{r.rule_code}]{r.label ? ` ${r.label}:` : ''} {r.detail ?? ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {ln.odoo_lot_id && (
                    <a
                      href={odooLotUrl(ln.odoo_lot_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-link hover:underline"
                    >
                      Lot {ln.odoo_lot_name ?? ln.odoo_lot_id} →
                    </a>
                  )}

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-role-metadata">CofA</span>
                      {canManageDocuments ? (
                        <AttachLink label="+ Attach" onFile={(f) => handleUploadCoa(ln.id, f)} />
                      ) : null}
                    </div>
                    {(ln.coa_documents ?? []).length === 0 ? (
                      <p className="text-xs text-tertiary">None attached.</p>
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {ln.coa_documents.map((d) => (
                          <DocumentRow
                            key={d.document_id}
                            doc={d}
                            onRemove={canManageDocuments ? () => handleRemoveCoa(ln.id, d.document_id) : undefined}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Resolution note — genuinely vendor-facing (reject_submission's
              reason), shown to everyone who can see the submission. */}
          {current.resolution_note && (
            <div className="rounded border border-border-subtle bg-surface-sunk p-3 text-sm text-secondary">
              <span className="font-medium text-primary">Resolution: </span>
              {current.resolution_note}
            </div>
          )}

          {/* Internal note — ops' own approve-exception rationale. Never
              serialized for a vendor caller (see api/types.ts), so this
              block is naturally a no-op for isVendorView, but the explicit
              guard documents the intent. */}
          {!isVendorView && current.internal_note && (
            <div className="rounded border border-border-subtle bg-surface-sunk p-3 text-sm text-secondary">
              <span className="font-medium text-primary">Internal note (not shown to vendor): </span>
              {current.internal_note}
            </div>
          )}

          {/* What to do — server-classified guidance per failed rule, so the
              reviewer never has to guess which button applies. */}
          {canApprove && !isVendorView && isPendingReview && mode === 'view' && failedRules.length > 0 && (
            <div className="flex flex-col gap-2 rounded border border-border bg-surface p-3">
              <p className="text-sm font-medium text-primary">What to do</p>
              {failedRules.map((r) => (
                <p key={r.rule_code} className="text-sm text-secondary">
                  <span className="font-medium text-primary">{r.rule_code}</span> — {r.action}
                  {r.kind === 'missing_cofa' && (
                    <> Missing for lot{lotsMissingCofa.length > 1 ? 's' : ''} {lotsMissingCofa.join(', ')}.</>
                  )}
                </p>
              ))}
            </div>
          )}

          {/* SPI actions */}
          {canApprove && isPendingReview && mode === 'view' && (
            <div className="flex flex-wrap gap-3">
              {/* Re-run is the primary action for anything fixable in Odoo. */}
              <Button
                variant={blockingFix || lotsMissingCofa.length > 0 ? 'primary' : 'ghost'}
                size="sm"
                onClick={handleRetry}
                disabled={retry.isPending}
              >
                <RefreshCw size={12} />
                {retry.isPending ? 'Re-running…' : 'Re-run validation'}
              </Button>
              {hasDuplicateFailure && (
                <Button variant="primary" size="sm" onClick={() => setMode('closing_duplicate')}>
                  Mark as duplicate (Posted)
                </Button>
              )}
              {lotsMissingCofa.length === 0 && (
                <Button
                  variant={blockingFix || hasDuplicateFailure ? 'ghost' : 'primary'}
                  size="sm"
                  onClick={() => setMode('approving')}
                >
                  {blockingFix ? 'Approve anyway…' : 'Approve anyway (SPI decision)'}
                </Button>
              )}
              <Button variant="danger" size="sm" onClick={() => setMode('rejecting')}>
                Reject and notify Uviva
              </Button>
            </div>
          )}

          {canApprove && mode === 'approving' && (
            <div className="flex flex-col gap-3 rounded border border-border bg-surface p-4">
              {blockingFix && (
                <p className="rounded border border-danger-border bg-danger-subtle/40 p-2 text-sm text-danger">
                  This will stage the lots and quantities on the receipt exactly as it is in Odoo now
                  (wrong routing) and does not fix it. Only continue if you have decided the routing is
                  acceptable — otherwise fix it in Odoo and use Re-run validation.
                </p>
              )}
              <p className="text-sm font-medium text-primary">Internal note (optional — ops only, never shown to the vendor)</p>
              <textarea
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                className="h-20 w-full rounded border border-border bg-canvas px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                placeholder="Reason for approving despite validation failures…"
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApprove}
                  disabled={approve.isPending}
                >
                  {approve.isPending ? 'Approving…' : 'Confirm approval'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setMode('view')}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {canApprove && mode === 'rejecting' && (
            <div className="flex flex-col gap-3 rounded border border-danger-border bg-danger-subtle/40 p-4">
              <p className="text-sm font-medium text-danger">Rejection reason (required — sent to Uviva)</p>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                className="h-20 w-full rounded border border-border bg-canvas px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                placeholder="Explain why this submission cannot be accepted…"
              />
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleReject}
                  disabled={reject.isPending || !rejectNote.trim()}
                >
                  {reject.isPending ? 'Rejecting…' : 'Confirm rejection'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setMode('view')}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {canApprove && (isPostFailed || isValidationFailed) && mode === 'view' && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-3">
                {/* Retrying a NO_PROCESSABLE_RECEIPT failure is guaranteed to
                    fail identically every time — de-emphasize Retry (ghost,
                    not primary) so it doesn't read as "just try again". */}
                <Button
                  variant={isLikelyDuplicate ? 'ghost' : 'primary'}
                  size="sm"
                  onClick={handleRetry}
                  disabled={retry.isPending}
                >
                  <RefreshCw size={12} />
                  {retry.isPending ? 'Retrying…' : isValidationFailed ? 'Retry validation' : 'Retry posting'}
                </Button>
                {/* 2026-08-04: for a confirmed duplicate, Reject is the WRONG
                    resolution — it tells the vendor to fix/resubmit something
                    that already arrived. Mark as duplicate closes it as
                    Posted instead, which is what actually happened. */}
                <Button
                  variant={isLikelyDuplicate ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setMode('closing_duplicate')}
                >
                  Mark as duplicate (Posted)
                </Button>
                <Button variant="danger" size="sm" onClick={() => setMode('rejecting')}>
                  Reject
                </Button>
              </div>
              {isLikelyDuplicate && (
                <p className="text-xs text-tertiary">
                  This looks like a duplicate — the material already appears received under a
                  different shipment. Mark it as a duplicate rather than retrying (will fail the
                  same way every time) or rejecting (tells the vendor to resubmit something that
                  already arrived).
                </p>
              )}
            </div>
          )}

          {canApprove && mode === 'closing_duplicate' && (
            <div className="flex flex-col gap-3 rounded border border-border bg-surface p-4">
              <p className="text-sm font-medium text-primary">
                Duplicate explanation (required — shown to the vendor as the reason; closes as Posted)
              </p>
              <textarea
                value={duplicateNote}
                onChange={(e) => setDuplicateNote(e.target.value)}
                className="h-20 w-full rounded border border-border bg-canvas px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                placeholder="e.g. Duplicate of SH2607-0225 — already received against this PO line…"
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCloseAsDuplicate}
                  disabled={closeAsDuplicate.isPending || !duplicateNote.trim()}
                >
                  {closeAsDuplicate.isPending ? 'Closing…' : 'Confirm — mark Posted'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setMode('view')}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  )
}
